import { ClipboardList, LucideIcon, Megaphone, PieChart, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Card } from "@/components/ui/v2/card";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/lib/utils/cn";

interface QuickAction {
  to: string;
  icon: LucideIcon;
  titleKey: string;
  subtitleKey: string;
}

export function QuickActionsSection({ clientId }: { clientId?: string }) {
  const { t } = useTranslation();
  const prefix = clientId ? `/${ROUTES.clients}/${clientId}` : "";

  const actions: QuickAction[] = [
    {
      to: `${prefix}/${ROUTES.campaigns}`,
      icon: Megaphone,
      titleKey: "home.quickActions.campaign.title",
      subtitleKey: "home.quickActions.campaign.subtitle",
    },
    {
      to: `${prefix}/${ROUTES.segments}`,
      icon: PieChart,
      titleKey: "home.quickActions.segments.title",
      subtitleKey: "home.quickActions.segments.subtitle",
    },
    {
      to: `${prefix}/${ROUTES.reporting}`,
      icon: TrendingUp,
      titleKey: "home.quickActions.reports.title",
      subtitleKey: "home.quickActions.reports.subtitle",
    },
    {
      to: `${prefix}/${ROUTES.applies}`,
      icon: ClipboardList,
      titleKey: "home.quickActions.applies.title",
      subtitleKey: "home.quickActions.applies.subtitle",
    },
  ];

  return (
    <section className="flex flex-col gap-4 w-full grow">
      <header className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold text-foreground">
          {t("home.quickActions.title")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("home.quickActions.subtitle")}
        </p>
      </header>
      <div className="flex justify-between flex-col w-full gap-4 h-full">
        {actions.map(({ to, icon: Icon, titleKey, subtitleKey }) => (
          <Link
            key={to}
            to={to}
            className={cn(
              "block grow rounded-lg outline-none transition",
              "focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            <Card hover className="flex h-full flex-row items-center gap-3 p-6">
              <div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Icon className="size-5" aria-hidden />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-base font-semibold text-foreground">
                  {t(titleKey)}
                </span>
                <span className="text-sm text-muted-foreground">
                  {t(subtitleKey)}
                </span>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </section>
  );
}
